import React, { useState, useEffect, useRef } from 'react';
import { MdSend, MdMic, MdMicOff, MdStop } from 'react-icons/md';

const ChatInput = ({ onSend, isLoading, onStop, readCode, onReadCodeChange }) => {
  const [inputText, setInputText] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [alwaysListening, setAlwaysListening] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    // Initialize SpeechRecognition if available
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false; // We handle continuous ourselves to ensure stability
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInputText((prev) => (prev ? prev + ' ' + transcript : transcript));
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error', event.error);
        if (event.error !== 'no-speech') {
          setIsListening(false);
          setAlwaysListening(false);
        }
      };

      recognitionRef.current.onend = () => {
        // If always listening is enabled, we automatically restart it when it ends
        // (Browser often stops it on its own after silence)
        if (alwaysListening && !isLoading) {
          try {
            recognitionRef.current.start();
          } catch(e) {
            console.error('Failed to restart speech recognition', e);
            setIsListening(false);
          }
        } else {
          setIsListening(false);
        }
      };
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, [alwaysListening, isLoading]);

  const handleSend = () => {
    const trimmed = inputText.trim();
    if (!trimmed || isLoading) return;
    
    onSend(trimmed);
    setInputText('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const toggleListen = () => {
    if (isListening) {
      setAlwaysListening(false); // Manually stopping disables always listening
      recognitionRef.current?.stop();
      setIsListening(false);
    } else {
      if (recognitionRef.current) {
        recognitionRef.current.start();
        setIsListening(true);
      } else {
        alert("Your browser does not support Speech Recognition.");
      }
    }
  };

  return (
    <div className="chatInputArea">
      <div className="togglesContainer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '16px', marginBottom: '8px', fontSize: '12px', color: 'var(--text-secondary)' }}>
        <div className="alwaysListeningToggle" style={{ display: 'flex', alignItems: 'center' }}>
          <label className="custom-toggle">
            <input 
              type="checkbox" 
              id="alwaysListen" 
              checked={alwaysListening} 
              onChange={(e) => {
                const checked = e.target.checked;
                setAlwaysListening(checked);
                if (checked && !isListening) {
                  toggleListen();
                }
              }} 
              disabled={isLoading}
            />
            <span className="toggle-slider"></span>
          </label>
          <label htmlFor="alwaysListen" style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}><MdMic size={14} /> Always Listening</label>
        </div>

        <div className="readCodeToggle" style={{ display: 'flex', alignItems: 'center' }}>
          <label className="custom-toggle">
            <input 
              type="checkbox" 
              id="readCodeToggle"
              checked={readCode} 
              onChange={(e) => onReadCodeChange(e.target.checked)} 
            />
            <span className="toggle-slider"></span>
          </label>
          <label htmlFor="readCodeToggle" style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>Read Editor Code</label>
        </div>
      </div>
      <div className="chatInputWrapper">
        <button 
          className={`btn iconBtn micBtn ${isListening ? 'listening' : ''}`} 
          onClick={toggleListen}
          title={isListening ? "Stop Listening" : "Start Voice Input"}
        >
          {isListening ? <MdMicOff size={18} /> : <MdMic size={18} />}
        </button>
        <textarea
          className="chatInput"
          placeholder="Ask a question..."
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          maxLength={1000}
          rows={1}
          disabled={isLoading}
        />
        {isLoading ? (
          <button 
            className="sendBtn" 
            onClick={onStop}
            title="Stop Generating"
            style={{ background: 'var(--danger)', boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)' }}
          >
            <MdStop size={22} />
          </button>
        ) : (
          <button 
            className="sendBtn" 
            onClick={handleSend}
            disabled={!inputText.trim()}
            title="Send"
          >
            <MdSend size={18} />
          </button>
        )}
      </div>
    </div>
  );
};

export default ChatInput;
